import { z } from "zod";
import type { Json } from "@/integrations/supabase/types";

const text = z.string();
const panelSchema = z.object({
  eyebrow: text, eyebrow_ar: text,
  heading: text, heading_ar: text,
  description: text, description_ar: text,
  cta_text: text, cta_text_ar: text,
  cta_link: text,
  helper: text, helper_ar: text,
  mobile_eyebrow: text.optional(), mobile_eyebrow_ar: text.optional(),
  mobile_heading: text.optional(), mobile_heading_ar: text.optional(),
  mobile_description: text.optional(), mobile_description_ar: text.optional(),
  mobile_cta_text: text.optional(), mobile_cta_text_ar: text.optional(),
  // image_url = desktop 16:9; mobile_image_url = mobile 9:16. Each falls back to the other.
  image_url: text.default(""),
  mobile_image_url: text.default(""),
  card_clickable: z.boolean().default(true),
  desktop_focal: z.object({ x: z.number().min(0).max(100), y: z.number().min(0).max(100) }).optional(),
  mobile_focal: z.object({ x: z.number().min(0).max(100), y: z.number().min(0).max(100) }).optional(),
  image_alt: text.default(""), image_alt_ar: text.default(""),
});
export const journeyBannerSchema = z.object({
  heading: text, heading_ar: text,
  introduction: text, introduction_ar: text,
  mobile_introduction: text.optional(), mobile_introduction_ar: text.optional(),
  personal: panelSchema,
  corporate: panelSchema,
});
export type JourneyBannerContent = z.infer<typeof journeyBannerSchema>;
export type JourneyPanel = z.infer<typeof panelSchema>;

// An absent/empty mobile override inherits only its own language's desktop text.
export function journeyText(desktop: string, mobile?: string): string {
  return mobile?.trim() ? mobile : desktop;
}

export function emptyJourneyBanner(): JourneyBannerContent {
  const panel = (): JourneyPanel => ({ eyebrow: "", eyebrow_ar: "", heading: "", heading_ar: "", description: "", description_ar: "", cta_text: "", cta_text_ar: "", cta_link: "", helper: "", helper_ar: "", image_url: "", mobile_image_url: "", card_clickable: true, image_alt: "", image_alt_ar: "" });
  return { heading: "", heading_ar: "", introduction: "", introduction_ar: "", personal: panel(), corporate: panel() };
}
// Staff wizard routes (Admin/Salesman sign-in required).
export const PERSONAL_WIZARD_ROUTE = "/salesman/perfume-wizard";
export const CORPORATE_WIZARD_ROUTE = "/salesman/corporate-gifting-wizard";
// Public customer entry routes for the same wizards; defaults for new dual banners.
export const PUBLIC_PERSONAL_WIZARD_ROUTE = "/perfume-wizard";
export const PUBLIC_CORPORATE_WIZARD_ROUTE = "/corporate-gifting-wizard";
export function newDualBanner(): JourneyBannerContent {
  const c = emptyJourneyBanner();
  c.personal = { ...c.personal, cta_text: "Start Your Perfume Journey", cta_text_ar: "ابدأ رحلتك مع العطر", cta_link: PUBLIC_PERSONAL_WIZARD_ROUTE };
  c.corporate = { ...c.corporate, cta_text: "Explore Corporate Gifting", cta_text_ar: "استكشف هدايا الشركات", cta_link: PUBLIC_CORPORATE_WIZARD_ROUTE };
  return c;
}
export function readJourneyBanner(value: unknown): JourneyBannerContent {
  const parsed = journeyBannerSchema.safeParse(value);
  return parsed.success ? parsed.data : emptyJourneyBanner();
}

// Parsing is for rendering only; editor writes retain unrelated stored extensions.
export function withJourneyContent(value: unknown, content: JourneyBannerContent): { [key: string]: Json } {
  const old = value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, Json> : {};
  const panel = (key: "personal" | "corporate") => {
    const previous = old[key];
    return { ...(previous && typeof previous === "object" && !Array.isArray(previous) ? previous : {}), ...content[key] };
  };
  return { ...old, ...content, personal: panel("personal"), corporate: panel("corporate") };
}

// Stored links are editable data, not executable markup. Unset/unsafe links stay disabled.
export function journeyDestination(value: string): string | null {
  const link = value.trim();
  if (!link || /[\s\\\u0000-\u001f]/.test(link)) return null;
  if (link.startsWith("/") && !link.startsWith("//")) return link;
  try {
    const url = new URL(link);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}
// Fragrance-journey panels only: saved links to the staff wizards (from before public entry existed) open the
// matching public wizard, so homepage visitors never land on staff sign-in. Other links are left unchanged.
const PUBLIC_WIZARD_FOR: Record<string, string> = {
  [PERSONAL_WIZARD_ROUTE]: PUBLIC_PERSONAL_WIZARD_ROUTE,
  [CORPORATE_WIZARD_ROUTE]: PUBLIC_CORPORATE_WIZARD_ROUTE,
};
export function journeyPanelDestination(value: string): string | null {
  const link = journeyDestination(value);
  return link ? PUBLIC_WIZARD_FOR[link.replace(/\/+$/, "")] ?? link : null;
}
